/**
 * API client functions for treatment catalog operations
 */

import { API_BASE_URL } from '../config/api';

export interface TreatmentCatalogItem {
  id: string;
  practice_id?: string | null;
  based_on_standard_id?: string | null;
  name: string;
  treatment_type: string;
  description?: string | null;
  default_duration_minutes?: number | null;
  requires_sedation: boolean;
  sedation_notes?: string | null;
  notes?: string | null;
  is_active: boolean;
  is_stock_tracked?: boolean;
  is_standard: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface TreatmentCatalogCreate {
  name: string;
  treatment_type: string;
  description?: string | null;
  default_duration_minutes?: number | null;
  requires_sedation?: boolean;
  sedation_notes?: string | null;
  notes?: string | null;
  is_stock_tracked?: boolean;
  display_order?: number;
  based_on_standard_id?: string;
}

export interface TreatmentCatalogUpdate {
  name?: string;
  treatment_type?: string;
  description?: string | null;
  default_duration_minutes?: number | null;
  requires_sedation?: boolean;
  sedation_notes?: string | null;
  notes?: string | null;
  is_active?: boolean;
  is_stock_tracked?: boolean;
  display_order?: number;
}

export interface TreatmentReferencesResponse {
  has_references: boolean;
  reference_count: number;
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
}

export async function getTreatmentCatalogItems(
  practiceId: string,
  includeInactive: boolean = false,
  search?: string
): Promise<TreatmentCatalogItem[]> {
  const params = new URLSearchParams();
  if (includeInactive) params.append('include_inactive', 'true');
  if (search) params.append('search', search);
  
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/treatments?${params.toString()}`,
    { method: 'GET', headers: getAuthHeaders() }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch treatments' }));
    throw new Error(error.detail || 'Failed to fetch treatments');
  }

  return response.json();
}

export async function getTreatmentCatalogItem(
  practiceId: string,
  itemId: string
): Promise<TreatmentCatalogItem> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/treatments/${itemId}`,
    { method: 'GET', headers: getAuthHeaders() }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch treatment' }));
    throw new Error(error.detail || 'Failed to fetch treatment');
  }

  return response.json();
}

export async function createTreatmentCatalogItem(
  practiceId: string,
  data: TreatmentCatalogCreate
): Promise<TreatmentCatalogItem> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/treatments`,
    { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to create treatment' }));
    throw new Error(error.detail || 'Failed to create treatment');
  }

  return response.json();
}

export async function updateTreatmentCatalogItem(
  practiceId: string,
  itemId: string,
  data: TreatmentCatalogUpdate
): Promise<TreatmentCatalogItem> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/treatments/${itemId}`,
    { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to update treatment' }));
    throw new Error(error.detail || 'Failed to update treatment');
  }

  return response.json();
}

export async function deleteTreatmentCatalogItem(
  practiceId: string,
  itemId: string
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/treatments/${itemId}`,
    { method: 'DELETE', headers: getAuthHeaders() }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete treatment' }));
    throw new Error(error.detail || 'Failed to delete treatment');
  }
}

export async function getTreatmentReferences(
  practiceId: string,
  itemId: string
): Promise<TreatmentReferencesResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/treatments/${itemId}/references`,
    { method: 'GET', headers: getAuthHeaders() }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to check references' }));
    throw new Error(error.detail || 'Failed to check references');
  }

  return response.json();
}

export async function hardDeleteTreatmentCatalogItem(
  practiceId: string,
  itemId: string
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/treatments/${itemId}/hard`,
    { method: 'DELETE', headers: getAuthHeaders() }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete treatment' }));
    throw new Error(error.detail || 'Failed to delete treatment');
  }
}
